<script setup>
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  watch,
  nextTick,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const attachmentDataUrl = computed(
  () => props.attachment?.dataUrl || props.attachment?.data_url || ''
);

const timeStampURL = computed(() => {
  if (!attachmentDataUrl.value) return '';
  return timeStampAppendedURL(attachmentDataUrl.value);
});

const TRANSCRIPT_PREVIEW_LENGTH = 200;
const isTranscriptExpanded = ref(false);
const isTranscriptLong = computed(
  () =>
    (props.attachment?.transcribedText?.length || 0) > TRANSCRIPT_PREVIEW_LENGTH
);
const displayedTranscript = computed(() => {
  const text = props.attachment?.transcribedText || '';
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const fallbackDurationSeconds = computed(() => {
  const meta = props.attachment?.meta;
  return meta?.durationSeconds ?? meta?.duration_seconds ?? null;
});

const displayDuration = computed(() => {
  if (Number.isFinite(duration.value) && duration.value > 0) {
    return duration.value;
  }
  return fallbackDurationSeconds.value || 0;
});

const serverDataUrl = computed(
  () =>
    props.attachment?.serverDataUrl || props.attachment?.server_data_url || ''
);

const usingServerFallback = ref(false);

const activeAudioUrl = computed(() => {
  if (usingServerFallback.value && serverDataUrl.value) {
    return timeStampAppendedURL(serverDataUrl.value);
  }
  return timeStampURL.value;
});

const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

const resetPlaybackState = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  duration.value = 0;
  playbackSpeed.value = 1;
  usingServerFallback.value = false;
};

const reloadAudioSource = async () => {
  resetPlaybackState();
  await nextTick();
  audioPlayer.value?.load();
};

const applyDuration = rawDuration => {
  if (Number.isFinite(rawDuration) && rawDuration > 0) {
    duration.value = rawDuration;
    return;
  }

  if (fallbackDurationSeconds.value) {
    duration.value = fallbackDurationSeconds.value;
  }
};

// MediaRecorder-produced WebM/Opus blobs lack a Duration header → <audio>.duration
// resolves to Infinity until we seek past the end, which forces the engine to
// scan the file and compute the real length. Safe no-op for files with a real
// duration already (mp3/m4a/etc).
const resolveStreamingDuration = () => {
  const el = audioPlayer.value;
  if (!el) return;
  const onTimeUpdate = () => {
    el.removeEventListener('timeupdate', onTimeUpdate);
    el.currentTime = 0;
    applyDuration(el.duration);
  };
  el.addEventListener('timeupdate', onTimeUpdate);
  try {
    el.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    el.removeEventListener('timeupdate', onTimeUpdate);
  }
};

const onLoadedMetadata = () => {
  const d = audioPlayer.value?.duration;
  if (!Number.isFinite(d)) {
    resolveStreamingDuration();
    return;
  }
  applyDuration(d);
};

const onDurationChange = () => {
  applyDuration(audioPlayer.value?.duration);
};

const onAudioError = () => {
  if (!usingServerFallback.value && serverDataUrl.value) {
    usingServerFallback.value = true;
    reloadAudioSource();
  }
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

watch(timeStampURL, (newUrl, oldUrl) => {
  if (newUrl && newUrl !== oldUrl) {
    usingServerFallback.value = false;
    reloadAudioSource();
  }
});

watch(fallbackDurationSeconds, seconds => {
  if (seconds && (!Number.isFinite(duration.value) || duration.value <= 0)) {
    duration.value = seconds;
  }
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  applyDuration(audioPlayer.value?.duration);
  if (audioPlayer.value) {
    audioPlayer.value.playbackRate = playbackSpeed.value;
  }
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (Number.isNaN(time) || !Number.isFinite(time) || time <= 0) {
    return '00:00';
  }
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const playbackTimeLabel = computed(() =>
  [formatTime(currentTime.value), formatTime(displayDuration.value)].join(' / ')
);

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, extension } = props.attachment;
  downloadFile({
    url: attachmentDataUrl.value,
    type: fileType,
    extension,
  });
};
</script>

<template>
  <audio
    :key="activeAudioUrl"
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @durationchange="onDurationChange"
    @error="onAudioError"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
    <source :src="activeAudioUrl" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div class="flex gap-1 w-full flex-1 items-center justify-start">
      <button class="p-0 border-0 size-8" @click="playOrPause">
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ playbackTimeLabel }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="displayDuration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="props.attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ displayedTranscript }}
      <button
        v-if="isTranscriptLong"
        class="block mt-1 p-0 border-0 bg-transparent text-n-slate-11 hover:text-n-slate-12 font-medium"
        @click="isTranscriptExpanded = !isTranscriptExpanded"
      >
        {{
          isTranscriptExpanded
            ? $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_LESS')
            : $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_MORE')
        }}
      </button>
    </div>
  </div>
</template>
